/** Browser-side staging for chat threads until Supabase read-back verification succeeds. */

export type PendingThreadStatus = "local_only" | "syncing" | "synced_partial" | "verified";

export type SerializedChatMessage = {
  id: string;
  role: "user" | "assistant";
  content: unknown;
  timestamp: string;
  citations?: unknown[];
  contextCitations?: Record<number, unknown>;
  images?: Array<{ url: string; detail?: string }>;
  model?: string;
  autoSelected?: boolean;
  status?: string;
  errorMessage?: string;
  errorKind?: string;
};

export type PendingThreadMetadata = {
  contextMode: string;
  chatMode: string;
  agentId: string | null;
  linkedSpaceIds: string[];
  topicId: string | null;
  attachedGitHubRepos: Array<{ fullName: string; owner: string; name: string }>;
};

export type PendingThreadDraft = {
  draftId: string;
  threadId: string | null;
  userId: string;
  workspaceId: string | null;
  messages: SerializedChatMessage[];
  metadata: PendingThreadMetadata;
  status: PendingThreadStatus;
  lastSyncAttemptAt: string | null;
  lastError: string | null;
  createdAt: string;
  updatedAt: string;
};

const INDEX_PREFIX = "ds-pending-thread-index:";
const DRAFT_PREFIX = "ds-pending-thread:";
const DISMISS_PREFIX = "ds-pending-thread-dismissed:";
const MAX_TOTAL_BYTES = 4 * 1024 * 1024;
/** Treat stuck "syncing" drafts as recoverable after this age. */
const STALE_SYNCING_MS = 60_000;

function draftKey(userId: string, draftId: string): string {
  return `${DRAFT_PREFIX}${userId}:${draftId}`;
}

function indexKey(userId: string): string {
  return `${INDEX_PREFIX}${userId}`;
}

function readIndex(userId: string): string[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = localStorage.getItem(indexKey(userId));
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((x) => typeof x === "string") : [];
  } catch {
    return [];
  }
}

function writeIndex(userId: string, draftIds: string[]): void {
  if (typeof window === "undefined") return;
  try {
    if (draftIds.length === 0) {
      localStorage.removeItem(indexKey(userId));
    } else {
      localStorage.setItem(indexKey(userId), JSON.stringify(draftIds));
    }
  } catch {
    // quota / private mode
  }
}

function mirrorToSession(key: string, value: string | null): void {
  if (typeof window === "undefined") return;
  try {
    if (value == null) sessionStorage.removeItem(key);
    else sessionStorage.setItem(key, value);
  } catch {
    // ignore
  }
}

/** Strip large base64 blobs from message content for local storage. */
export function sanitizeMessagesForLocalStorage(
  messages: SerializedChatMessage[]
): SerializedChatMessage[] {
  return messages.map((m) => {
    const next = { ...m };
    if (typeof next.content === "string" && next.content.length > 50_000) {
      next.content = `${next.content.slice(0, 50_000)}\n...[truncated for local backup]`;
    }
    if (Array.isArray(next.images)) {
      next.images = next.images.map((img) =>
        img.url?.startsWith("data:") ? { ...img, url: "[image-stored-locally-truncated]" } : img
      );
    }
    return next;
  });
}

function estimateDraftBytes(draft: PendingThreadDraft): number {
  try {
    return new Blob([JSON.stringify(draft)]).size;
  } catch {
    return JSON.stringify(draft).length;
  }
}

function enforceSizeCap(userId: string): void {
  const ids = readIndex(userId);
  let total = 0;
  const drafts: Array<{ id: string; draft: PendingThreadDraft; bytes: number }> = [];
  for (const id of ids) {
    const draft = readPendingThreadDraft(userId, id);
    if (!draft || draft.status === "verified") continue;
    const bytes = estimateDraftBytes(draft);
    drafts.push({ id, draft, bytes });
    total += bytes;
  }
  if (total <= MAX_TOTAL_BYTES) return;
  drafts.sort((a, b) => a.draft.updatedAt.localeCompare(b.draft.updatedAt));
  for (const entry of drafts) {
    if (total <= MAX_TOTAL_BYTES) break;
    clearPendingThreadDraft(userId, entry.id);
    total -= entry.bytes;
  }
}

export function createDraftId(): string {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `draft-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

export function savePendingThreadDraft(draft: PendingThreadDraft): void {
  if (typeof window === "undefined" || !draft.userId) return;
  const sanitized: PendingThreadDraft = {
    ...draft,
    messages: sanitizeMessagesForLocalStorage(draft.messages),
    updatedAt: new Date().toISOString(),
  };
  const key = draftKey(draft.userId, draft.draftId);
  const payload = JSON.stringify(sanitized);
  try {
    localStorage.setItem(key, payload);
    mirrorToSession(key, payload);
    const ids = readIndex(draft.userId);
    if (!ids.includes(draft.draftId)) {
      writeIndex(draft.userId, [draft.draftId, ...ids]);
    }
    enforceSizeCap(draft.userId);
  } catch (e) {
    console.error("[pendingThreadStore] Failed to save draft:", e);
  }
}

export function readPendingThreadDraft(
  userId: string,
  draftId: string
): PendingThreadDraft | null {
  if (typeof window === "undefined") return null;
  const key = draftKey(userId, draftId);
  try {
    let raw = localStorage.getItem(key);
    if (!raw) raw = sessionStorage.getItem(key);
    if (!raw) return null;
    return JSON.parse(raw) as PendingThreadDraft;
  } catch {
    return null;
  }
}

export function clearPendingThreadDraft(userId: string, draftId: string): void {
  if (typeof window === "undefined") return;
  const key = draftKey(userId, draftId);
  try {
    localStorage.removeItem(key);
    mirrorToSession(key, null);
    const ids = readIndex(userId).filter((id) => id !== draftId);
    writeIndex(userId, ids);
  } catch {
    // ignore
  }
}

export function listUnverifiedPendingDrafts(userId: string): PendingThreadDraft[] {
  if (!userId) return [];
  const ids = readIndex(userId);
  const results: PendingThreadDraft[] = [];
  for (const id of ids) {
    const draft = readPendingThreadDraft(userId, id);
    if (draft && draft.status !== "verified") {
      results.push(draft);
    }
  }
  results.sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
  return results;
}

/**
 * Drafts that need the recover UI: real save failures (`local_only`) or stuck syncs.
 * Excludes `synced_partial` (already written to DB; turn may still be incomplete).
 */
export function listRecoverablePendingDrafts(userId: string): PendingThreadDraft[] {
  if (!userId) return [];
  const now = Date.now();
  const results: PendingThreadDraft[] = [];
  for (const draft of listUnverifiedPendingDrafts(userId)) {
    if (draft.status === "local_only") {
      results.push(draft);
      continue;
    }
    if (draft.status === "syncing") {
      const attemptAt = draft.lastSyncAttemptAt
        ? Date.parse(draft.lastSyncAttemptAt)
        : Date.parse(draft.updatedAt);
      if (!Number.isNaN(attemptAt) && now - attemptAt > STALE_SYNCING_MS) {
        results.push(draft);
      }
    }
  }
  return results;
}

function dismissKey(userId: string, draftId: string): string {
  return `${DISMISS_PREFIX}${userId}:${draftId}`;
}

/** Session-scoped dismiss so "Not now" does not remount-loop the recover dialog. */
export function isPendingDraftDismissed(userId: string, draftId: string): boolean {
  if (typeof window === "undefined" || !userId || !draftId) return false;
  try {
    return sessionStorage.getItem(dismissKey(userId, draftId)) === "1";
  } catch {
    return false;
  }
}

export function dismissPendingDraft(userId: string, draftId: string): void {
  if (typeof window === "undefined" || !userId || !draftId) return;
  try {
    sessionStorage.setItem(dismissKey(userId, draftId), "1");
  } catch {
    // ignore
  }
}

/** Drop local drafts for a thread once the DB row already has those messages. */
export function clearPendingDraftsCoveredByDb(
  userId: string,
  threadId: string,
  dbMessageCount: number
): void {
  if (!userId || !threadId) return;
  const drafts = listUnverifiedPendingDrafts(userId).filter(
    (d) => d.threadId != null && String(d.threadId) === String(threadId)
  );
  for (const draft of drafts) {
    if (draft.messages.length <= dbMessageCount) {
      clearPendingThreadDraft(userId, draft.draftId);
    }
  }
}

export function getFirstUserMessagePreview(draft: PendingThreadDraft): string {
  const userMsg = draft.messages.find((m) => m.role === "user");
  if (!userMsg) return "Unsaved conversation";
  const text =
    typeof userMsg.content === "string"
      ? userMsg.content
      : JSON.stringify(userMsg.content ?? "");
  const trimmed = text.trim();
  if (!trimmed) return "Unsaved conversation";
  return trimmed.length > 120 ? `${trimmed.slice(0, 120)}…` : trimmed;
}
